import {
  useWSSnackbar,
  WSActions,
  WSForm,
  WSInputPassword,
  WSList
} from "@wingspanhq/fe-component-library";
import React from "react";
import * as Yup from "yup";
import { useWSMutation } from "@ws-react-query";
import { queriesPersons } from "@query";
import { personsApi } from "@api";
import { sessionService } from "@services/sessionService";
import { useSession } from "@hooks/useSession";
import { ErrorAlert } from "@components/ErrorAlert";
import { validatorPassword } from "../../../../../shared/validators/validatorPassword";

type FormData = {
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
};

type Props = {
  onSuccess?: (data: FormData) => void;
  onClose: () => void;
};

export const FormChangePassword: React.FC<Props> = ({ onSuccess, onClose }) => {
  const { personId } = useSession();
  const queryPerson = queriesPersons.getPerson.useQuery({ personId });
  const email = queryPerson.data?.email || undefined;

  const { openSnackbar } = useWSSnackbar();

  const [mutate, meta] = useWSMutation<void, unknown, FormData>(
    async data => {
      if (!email) {
        throw new Error("Missing account email");
      }

      // Validate the current password by authenticating with it.
      await sessionService.login(email, data.currentPassword, true);

      // Set the new password.
      await personsApi.resetPersonPassword({
        personId,
        resetPersonPasswordRequest: { newPassword: data.newPassword }
      });

      // Re-establish the session with the new password.
      await sessionService.login(email, data.newPassword, true);
    },
    {
      dependencies: [queriesPersons.getPerson.queryKeyBase],
      onSuccess: (_, data) => {
        onSuccess?.(data);
        onClose();

        openSnackbar({
          message: "Your password has been successfully updated.",
          type: "success"
        });
      }
    }
  );

  return (
    <WSForm<FormData>
      onSubmit={mutate}
      defaultValues={{
        currentPassword: "",
        newPassword: "",
        confirmNewPassword: ""
      }}
      validationSchema={Yup.object({
        currentPassword: validatorPassword.required("Required"),
        newPassword: validatorPassword.required("Required"),
        confirmNewPassword: validatorPassword
          .required("Required")
          .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
      })}
    >
      <WSList gap="L">
        <WSForm.Field
          name="currentPassword"
          label="Current Password"
          component={WSInputPassword}
        />

        <WSForm.Field
          name="newPassword"
          label="New Password"
          component={WSInputPassword}
        />

        <WSForm.Field
          name="confirmNewPassword"
          label="Confirm New Password"
          component={WSInputPassword}
        />

        <ErrorAlert context="PasswordReset" error={meta.error} />

        <WSActions
          buttons={[
            {
              label: "Update"
            },
            {
              label: "Cancel",
              kind: "Secondary"
            }
          ]}
        />
      </WSList>
    </WSForm>
  );
};
